/**
 * Which meeting instances this viewer has clicked Join for (#14931).
 *
 * Joining cannot be detected, so "has joined" is "clicked Join in Cadence",
 * remembered per viewer IN THIS BROWSER: one `localStorage` entry mapping
 * `meetingJoinKey` (a digest of the conference URL — never the URL, which is
 * a credential to the room — plus the instance's start) to the instant the
 * entry expires — the instance's end. Expired entries are pruned on every
 * read and every write, so nothing outlives its meeting in the browser.
 *
 * Every read and write is wrapped: a browser that refuses storage (private
 * mode, a quota, a sandboxed frame) reads "not joined" rather than throwing
 * into a render. A click is also kept in memory for this page, so the button
 * still goes away where storage refuses to keep it.
 *
 * Readers subscribe through `useMeetingJoined`: a click notifies every
 * mounted surface on the page, and another tab's write arrives through the
 * `storage` event.
 *
 * Client-safe and SSR-safe: nothing touches `window` until called.
 */

import {
  type JoinableConference,
  meetingJoinExpiresAtMs,
  meetingJoinKey,
} from './meeting-join'

export const MEETING_JOINED_STORAGE_KEY = 'cadence.meeting-joined.v1'

type Entries = Record<string, number>

/** This page's clicks, whatever storage did with them. */
const memory = new Map<string, number>()
/** Bumped on every local write, so the snapshot moves even without storage. */
let version = 0
const listeners = new Set<() => void>()

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

function readRaw(): string | null {
  try {
    return storage()?.getItem(MEETING_JOINED_STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

function readStored(): Entries {
  const raw = readRaw()
  if (!raw) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))
      return {}
    const entries: Entries = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        entries[key] = value
      }
    }
    return entries
  } catch {
    return {}
  }
}

/**
 * The stored entries still live at `nowMs`, writing the pruned set back when
 * anything had expired. The write is idempotent, so a read that prunes once
 * changes the snapshot once and then settles.
 */
function readLive(nowMs: number): Entries {
  const stored = readStored()
  const kept: Entries = {}
  let pruned = false
  for (const [key, at] of Object.entries(stored)) {
    if (at > nowMs) kept[key] = at
    else pruned = true
  }
  if (pruned) {
    try {
      storage()?.setItem(MEETING_JOINED_STORAGE_KEY, JSON.stringify(kept))
    } catch {
      // Storage refused: the expired entries read as not joined anyway.
    }
  }
  return kept
}

/** Whether the viewer clicked Join for this instance, and it has not ended. */
export function isMeetingJoined(
  conference: JoinableConference,
  nowMs: number,
): boolean {
  const key = meetingJoinKey(conference)
  const expiresAt = memory.get(key) ?? readLive(nowMs)[key]
  return expiresAt !== undefined && expiresAt > nowMs
}

/** Record the click, prune what has expired, and tell every reader. */
export function markMeetingJoined(
  conference: JoinableConference,
  nowMs: number,
): void {
  const key = meetingJoinKey(conference)
  const expiresAt = meetingJoinExpiresAtMs(conference)
  memory.set(key, expiresAt)
  try {
    const kept: Entries = { ...readLive(nowMs), [key]: expiresAt }
    storage()?.setItem(MEETING_JOINED_STORAGE_KEY, JSON.stringify(kept))
  } catch {
    // Storage refused: the in-memory click still hides it on this page.
  }
  version += 1
  for (const listener of [...listeners]) listener()
}

/** `useSyncExternalStore`'s subscribe: local writes and other tabs'. */
export function subscribeMeetingJoined(listener: () => void): () => void {
  listeners.add(listener)
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === MEETING_JOINED_STORAGE_KEY) {
      listener()
    }
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage)
  }
  return () => {
    listeners.delete(listener)
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage)
    }
  }
}

/**
 * A string, so `useSyncExternalStore` compares it by value: it changes when
 * this page writes or the stored entry does, and at no other time.
 */
export function getMeetingJoinedSnapshot(): string {
  return `${version}:${readRaw() ?? ''}`
}

/** FOR TESTS — and a fresh page: forget this page's clicks and readers. */
export function resetMeetingJoinedStore(): void {
  memory.clear()
  version = 0
  listeners.clear()
}
